import { Helmet } from "react-helmet";
import ResizableHeader from "@/components/ResizableHeader";
import PricingSection from '@/components/ui/pricing-section';
import { FaqsAccordion } from '@/components/ui/faqs-accordion';
import CallToAction from '@/components/CallToAction';

export function Pricing() {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Pricing | BankConvert</title>
        <meta name="description" content="Simple, transparent pricing for converting bank statement PDFs to Excel. Free tier available, paid plans for higher volume." />
        <link rel="canonical" href="https://www.bankconvert.org/pricing" />
      </Helmet>
      <ResizableHeader />
      {/* Use the PricingSection component */}
      <PricingSection />
      
      {/* Call to Action Section */}
      <CallToAction />
      
      {/* Internal links for SEO */}
      <section className="py-10 bg-muted/30">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-lg font-semibold mb-4">Bank Statement Conversion Guides</h2>
          <ul className="space-y-2">
            <li>
              <a href="/blog/convert-pdf-bank-statement-to-excel" className="text-primary hover:underline">
                How to convert PDF bank statements to Excel
              </a>
            </li>
            <li>
              <a href="/blog/csv-to-excel-converter-free-instant-private" className="text-primary hover:underline">
                Free private CSV to Excel converter — no upload required
              </a>
            </li>
            <li>
              <a href="/blog/announcing-paid-plans-bank-statement-pdf-converter" className="text-primary hover:underline">
                What's included in BankConvert paid plans
              </a>
            </li>
          </ul>
        </div>
      </section>

      {/* FAQ Section */}
      <div className="container mx-auto px-4 py-16">
        <FaqsAccordion
          title="Frequently Asked Questions"
          description="Find answers to common questions about our bank statement conversion service"
          faqs={[
            {
              question: "Does my bank statement get sent to your server?",
              answer:
                "No. The entire conversion happens inside your browser tab using JavaScript. Your PDF is never transmitted over the network. To verify: open DevTools → Network tab → convert a file. You'll see zero outbound requests carrying your file.",
            },
            {
              question: "How accurate is the conversion?",
              answer:
                "Very accurate on clean, text-based PDFs from major banks. Our browser-based parser extracts every row — dates, payees, debits, credits, and running balance. Scanned or image-based PDFs won't work because we don't use OCR. If you can select text in your PDF viewer, it will work here.",
            },
            {
              question: "My bank already offers CSV exports. Why use this?",
              answer:
                "If your bank gives you a clean CSV, use that — it's faster. BankConvert is for the cases where you only have a PDF: statements from accountants, older accounts, third-party records, or banks that don't offer data exports.",
            },
            {
              question: "Can't I just paste my PDF into ChatGPT?",
              answer:
                "Yes, that's a real alternative. BankConvert is built for repeatable bank statement extraction, with browser-based PDF parsing and spreadsheet-ready output.",
            },
            {
              question: "What file formats do you support?",
              answer:
                "Standard PDF bank statements with selectable text and clear transaction rows. Scanned image PDFs may need OCR first.",
            },
            {
              question: "Can I cancel anytime?",
              answer:
                "Yes. Cancel from your billing page at any time. You keep access until the end of the current billing period. No cancellation fees.",
            },
            {
              question: "How do I delete my account?",
              answer:
                "Go to Settings → Account → Delete Account. This removes your email, conversion history, subscription record, and saved account data.",
            },
          ]}
        />
      </div>
    </div>
  );
}
